import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import { Crop, HardDriveUpload, Replace, Trash2, TriangleAlert } from 'lucide-react';
import * as React from 'react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  StatusChip
} from '@/design';
import { LoadError } from '@/domains/admin';
import { ApiError, ProblemType } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import { mediaApi } from '../api/media-api';
import type { ContentImageKind, IContentImage, IContentPlacement } from '../api/media-contracts';
import { CropDialog } from '../crop-dialog';
import { FileName, Thumb, placeable, refreshLibrary, useLibrary } from '../library';
import { EmptyNote, formatBytes, personName } from '../shared';
import { ContentPage } from './content-page';

/**
 * The media library (WEB-09), over `/api/v1/admin/website/media`.
 *
 * **This was the screen furthest from being real, and it is now the one whose requirement is
 * answered most completely.** It drew tinted blocks instead of photographs, because there was no
 * object storage behind it and a convincing gallery of stock images is the most misleading thing
 * a mock can contain. The storage turned out to be there already — SR-223's public blob store,
 * where participants' photographs live — and what was missing was a library on top of it.
 *
 * The three criteria land on three controls, each drawn where it is met:
 *
 * - **AC1, upload.** The file is the request's body with its own type, as a participant photo is.
 *   Nothing is judged here: survey-api refuses an empty file, one over 5 MB, and one whose bytes
 *   are not the type they claim, and this screen says each refusal in its own words. A check in
 *   the browser would be a second judge of the server's rule and would hide the real answer.
 * - **AC2, replace.** Every tile carries its placements, and a replacement lists them with a box
 *   each. All ticked is the requirement's first branch — everything updates; some of them is its
 *   second — the editor was shown each placement and chose. The default is everything, because
 *   the case this exists for is a rebrand: the point of the list is that the exceptions are
 *   visible, not that the default is cautious.
 * - **AC3, crop.** In `crop-dialog.tsx`, in the browser, and the original is retained because
 *   nothing in the system rewrites an image's bytes.
 *
 * **Nothing deletes a picture out from under a page.** An image in use is refused with the names
 * of the places using it, which is the one refusal here an editor can act on without guessing.
 */

const KIND_LABEL: Record<ContentImageKind, string> = {
  photo: 'Photo',
  headshot: 'Headshot',
  logo: 'Logo',
  graphic: 'Graphic'
};

const PLACEMENT_LABEL: Record<IContentPlacement['kind'], string> = {
  'post-lead': 'Lead image',
  'post-social': 'Sharing image',
  'post-body': 'In the article',
  'team-photo': 'Team photograph',
  'event-social': 'Event sharing image',
  'event-speaker': 'Speaker headshot'
};

export function ContentMediaPage() {
  const { images, loading, error } = useLibrary();
  const [uploading, setUploading] = React.useState(false);
  const [replacing, setReplacing] = React.useState<Nullable<IContentImage>>(null);
  const [cropping, setCropping] = React.useState<Nullable<IContentImage>>(null);

  const current = placeable(images);
  const superseded = images.filter((image) => image.replacedByImageId !== null);

  return (
    <ContentPage
      title="Media"
      lede="Images used across the site, and where each one appears."
      actions={
        <Button size="sm" onClick={() => setUploading(true)}>
          <HardDriveUpload className="mr-1 h-4 w-4" aria-hidden />
          Upload
        </Button>
      }
    >
      {error && <LoadError message={error} />}

      <section className="grid gap-3">
        <h2 className="text-sm font-medium text-muted-foreground">In use ({current.length})</h2>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-4">
          {current.map((image) => (
            <ImageCard
              key={`${image.imageId}:${image.revision}`}
              image={image}
              onReplace={() => setReplacing(image)}
              onCrop={() => setCropping(image)}
            />
          ))}
        </div>
        {current.length === 0 && !loading && !error && (
          <EmptyNote>
            The library is empty. &ldquo;Upload&rdquo; puts the first image in it.
          </EmptyNote>
        )}
      </section>

      {superseded.length > 0 && (
        <section className="grid gap-2">
          <h2 className="text-sm font-medium text-muted-foreground">
            Superseded ({superseded.length})
          </h2>
          <p className="text-xs text-muted-foreground">
            Replaced everywhere they were used, and kept — AC3 asks that the original be retained.
          </p>
          <div className="flex flex-wrap gap-3">
            {superseded.map((image) => (
              <div key={image.imageId} className="grid w-40 grid-cols-1 gap-1">
                <Thumb image={image} size="lg" className="opacity-60" />
                <FileName name={image.fileName} className="block text-xs text-muted-foreground" />
              </div>
            ))}
          </div>
        </section>
      )}

      {uploading && <UploadDialog onDone={() => setUploading(false)} />}
      {replacing && (
        <ReplaceDialog image={replacing} library={current} onDone={() => setReplacing(null)} />
      )}
      {cropping && <CropDialog image={cropping} onDone={() => setCropping(null)} />}
    </ContentPage>
  );
}

/* ── one image ─────────────────────────────────────────────────────────────────────────── */

/**
 * Mounted with a `key` carrying the image's revision, so its fields are seeded once per version
 * of it — the remount-per-record rule the post and team editors follow, for the reason they
 * follow it: a form seeded on mount and never reset holds the previous value under the new name.
 */
function ImageCard({
  image,
  onReplace,
  onCrop
}: {
  image: IContentImage;
  onReplace: () => void;
  onCrop: () => void;
}) {
  const [alt, setAlt] = React.useState(image.alt);
  const [kind, setKind] = React.useState<ContentImageKind>(image.kind);
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const [confirming, setConfirming] = React.useState(false);
  const etag = `"${image.revision}"`;
  const placed = image.placements.length;

  const describe = async (next: { alt?: string; kind?: ContentImageKind }) => {
    const body = { fileName: image.fileName, kind: next.kind ?? kind, alt: next.alt ?? alt };
    if (body.alt === image.alt && body.kind === image.kind) return;
    setBusy(true);
    setRefusal(null);
    try {
      await mediaApi.save(image.imageId, etag, body);
      refreshLibrary();
    } catch (error) {
      setRefusal(refusalOf(error, 'The change could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setConfirming(false);
    setBusy(true);
    setRefusal(null);
    try {
      await mediaApi.remove(image.imageId, etag);
      refreshLibrary();
    } catch (error) {
      // A 404 is somebody else having deleted it: the library is read again rather than the
      // editor being invited to press a button that has nothing left to act on.
      if (error instanceof ApiError && error.status === 404) refreshLibrary();
      else setRefusal(refusalOf(error, 'The image could not be deleted.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="grid grid-cols-1 gap-2 p-3">
        <Thumb image={image} size="lg" />
        <div className="grid grid-cols-1 gap-1">
          <FileName name={image.fileName} className="block text-sm font-medium" />
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusChip tone="neutral">{KIND_LABEL[image.kind]}</StatusChip>
            <StatusChip tone={placed > 0 ? 'info' : 'neutral'}>
              {placed === 0 ? 'Unused' : `${placed} placement${placed === 1 ? '' : 's'}`}
            </StatusChip>
          </div>
          <span className="text-xs text-muted-foreground">
            {image.width}×{image.height} · {formatBytes(image.bytes)}
          </span>
          <span className="text-xs text-muted-foreground">
            {personName(image.createdBy)}, {formatDateTime(image.createdAt)}
          </span>
          {image.sourceImageId !== null && (
            <span className="text-xs text-muted-foreground">
              {image.crop ? 'Cropped from another image' : 'Made from another image'} — that one
              is kept.
            </span>
          )}
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor={`alt-${image.imageId}`} className="text-xs">
            Alternative text
          </Label>
          <Input
            id={`alt-${image.imageId}`}
            value={alt}
            placeholder="Describe the image"
            disabled={busy}
            onChange={(e) => setAlt(e.target.value)}
            onBlur={() => void describe({ alt })}
          />
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor={`kind-${image.imageId}`} className="text-xs">
            Kind
          </Label>
          <Select
            value={kind}
            disabled={busy}
            onValueChange={(value) => {
              const next = value as ContentImageKind;
              setKind(next);
              void describe({ kind: next });
            }}
          >
            <SelectTrigger id={`kind-${image.imageId}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(KIND_LABEL).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {placed > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">Where it is used</summary>
            <ul className="mt-1 grid gap-0.5 pl-3">
              {image.placements.map((placement) => (
                <li key={keyOf(placement)}>
                  {PLACEMENT_LABEL[placement.kind]} —{' '}
                  <span className="text-muted-foreground">{placement.label}</span>
                </li>
              ))}
            </ul>
          </details>
        )}

        {refusal && <p className="text-xs text-[var(--color-deep-magenta)]">{refusal}</p>}

        <div className="flex flex-wrap items-center gap-1.5">
          <Button variant="outline" size="sm" disabled={busy} onClick={onCrop}>
            <Crop className="mr-1 h-3 w-3" aria-hidden />
            Crop
          </Button>
          <Button variant="outline" size="sm" disabled={busy || placed === 0} onClick={onReplace}>
            <Replace className="mr-1 h-3 w-3" aria-hidden />
            Replace
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => setConfirming(true)}
            aria-label={`Delete ${image.fileName}`}
          >
            <Trash2 className="mr-1 h-3 w-3" aria-hidden />
            Delete
          </Button>
        </div>

        {confirming && (
          <Dialog open onOpenChange={(open) => !open && setConfirming(false)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Delete this image?</DialogTitle>
                <DialogDescription>
                  <span className="break-all">{image.fileName}</span>{' '}
                  {placed > 0
                    ? 'is used on the site, so this will be refused — replace it there first.'
                    : 'goes from the library. The stored file stays: it is named by its contents, and another image may be standing on the same bytes.'}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={() => void remove()}>
                  Delete the image
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
}

/* ── AC2: replacing an image, placement by placement ───────────────────────────────────── */

/**
 * Every placement starts ticked, because the common case during a rebrand is "everywhere". What
 * is left unticked keeps this image — which is why the old one stays in the library and is
 * marked superseded only when nothing is left on it.
 */
function ReplaceDialog({
  image,
  library,
  onDone
}: {
  image: IContentImage;
  library: IContentImage[];
  onDone: () => void;
}) {
  const [withImageId, setWithImageId] = React.useState<Nullable<string>>(null);
  const [chosen, setChosen] = React.useState<string[]>(image.placements.map(keyOf));
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const candidates = library.filter((candidate) => candidate.imageId !== image.imageId);
  const keeping = image.placements.length - chosen.length;

  const toggle = (key: string) =>
    setChosen((current) =>
      current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key]
    );

  const replace = async () => {
    if (!withImageId) return;
    setBusy(true);
    setRefusal(null);
    try {
      await mediaApi.replace(
        image.imageId,
        `"${image.revision}"`,
        withImageId,
        image.placements
          .filter((placement) => chosen.includes(keyOf(placement)))
          .map((placement) => ({ kind: placement.kind, itemId: placement.itemId }))
      );
      refreshLibrary();
      onDone();
    } catch (error) {
      setRefusal(refusalOf(error, 'Nothing was moved.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onDone()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex min-w-0 items-baseline gap-1">
            <span className="shrink-0">Replace</span>
            <FileName name={image.fileName} />
          </DialogTitle>
          <DialogDescription>
            Choose the image to use, then which of its {image.placements.length} placement
            {image.placements.length === 1 ? '' : 's'} to move. Both images are kept either way.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="max-h-[70dvh] overflow-y-auto">
          <div className="grid grid-cols-1 gap-2">
            <Label>Use this image instead</Label>
            <div className="grid max-h-56 grid-cols-[repeat(auto-fill,minmax(8rem,1fr))] gap-2 overflow-auto p-0.5">
              {candidates.map((candidate) => (
                <button
                  key={candidate.imageId}
                  type="button"
                  onClick={() => setWithImageId(candidate.imageId)}
                  className={`rounded-md border p-1 text-left ${
                    withImageId === candidate.imageId
                      ? 'border-[var(--color-sapphire)]'
                      : 'border-border'
                  }`}
                >
                  <Thumb image={candidate} size="lg" />
                  <FileName name={candidate.fileName} className="mt-1 block text-xs" />
                </button>
              ))}
              {candidates.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  There is no other image in the library — upload the new one first.
                </p>
              )}
            </div>
          </div>

          <fieldset className="grid grid-cols-1 gap-2">
            <legend className="text-sm font-medium">Placements to move</legend>
            {image.placements.map((placement) => (
              <label key={keyOf(placement)} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={chosen.includes(keyOf(placement))}
                  onCheckedChange={() => toggle(keyOf(placement))}
                />
                <span>
                  {PLACEMENT_LABEL[placement.kind]}{' '}
                  <span className="text-muted-foreground">({placement.label})</span>
                </span>
              </label>
            ))}
          </fieldset>

          {keeping > 0 && chosen.length > 0 && (
            <Alert>
              <TriangleAlert className="h-4 w-4" aria-hidden />
              <AlertTitle>
                {keeping} placement{keeping === 1 ? '' : 's'} will keep this image
              </AlertTitle>
              <AlertDescription>
                So <span className="break-all">{image.fileName}</span> stays in the library, and
                stays on the site.
              </AlertDescription>
            </Alert>
          )}

          {refusal && <p className="text-sm text-[var(--color-deep-magenta)]">{refusal}</p>}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onDone} disabled={busy}>
            Cancel
          </Button>
          <Button
            disabled={!withImageId || chosen.length === 0 || busy}
            onClick={() => void replace()}
          >
            {busy ? 'Moving…' : `Replace in ${chosen.length} place${chosen.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── AC1: the upload ───────────────────────────────────────────────────────────────────── */

/**
 * A real file picker, and the file itself on the wire.
 *
 * `accept` narrows the browser's dialog to the four formats survey-api takes, and that is a
 * convenience rather than a check: whatever is chosen is sent, and what this says back is the
 * server's own refusal.
 */
function UploadDialog({ onDone }: { onDone: () => void }) {
  const input = React.useRef<HTMLInputElement>(null);
  const [file, setFile] = React.useState<Nullable<File>>(null);
  const [fileName, setFileName] = React.useState('');
  const [kind, setKind] = React.useState<ContentImageKind>('photo');
  const [alt, setAlt] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);

  const choose = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0] ?? null;
    // Cleared so choosing the same file again — after a refusal, say — still fires.
    event.target.value = '';
    setFile(chosen);
    if (chosen) setFileName(chosen.name);
    setRefusal(null);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setRefusal(null);
    try {
      await mediaApi.upload(file, { fileName: fileName.trim() || file.name, kind, alt });
      refreshLibrary();
      onDone();
    } catch (error) {
      setRefusal(uploadRefusal(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onDone()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload an image</DialogTitle>
          <DialogDescription>
            A JPEG, PNG, GIF or WebP, up to 5 MB. It goes into the library, and can be placed on a
            post or a person afterwards.
          </DialogDescription>
        </DialogHeader>

        {/* `DialogBody`'s one column is `minmax(0, 1fr)`, and it is load-bearing here: a column
            left at `auto` is sized to its content's **max-content** width, which `truncate` does
            not reduce. One file named by a download folder would stretch it, and every field
            below it spill off the dialog. */}
        <DialogBody className="gap-3">
          <input
            ref={input}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="sr-only"
            onChange={choose}
          />
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              className="shrink-0"
              onClick={() => input.current?.click()}
            >
              {file ? 'Choose another file' : 'Choose a file'}
            </Button>
            {file ? (
              <span className="flex min-w-0 items-baseline gap-1 text-sm text-muted-foreground">
                <FileName name={file.name} />
                <span className="shrink-0">· {formatBytes(file.size)}</span>
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">No file chosen</span>
            )}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="upload-name">Name in the library</Label>
            <Input
              id="upload-name"
              value={fileName}
              placeholder="hero-banner.jpg"
              onChange={(e) => setFileName(e.target.value)}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="upload-kind">Kind</Label>
            <Select value={kind} onValueChange={(value) => setKind(value as ContentImageKind)}>
              <SelectTrigger id="upload-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(KIND_LABEL).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="upload-alt">Alternative text</Label>
            <Input id="upload-alt" value={alt} onChange={(e) => setAlt(e.target.value)} />
            <p className="text-xs text-muted-foreground">
              What a screen reader says in place of the picture. Leave it empty only where the
              image is decoration.
            </p>
          </div>

          {refusal && <p className="text-sm text-[var(--color-deep-magenta)]">{refusal}</p>}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onDone} disabled={busy}>
            Cancel
          </Button>
          <Button disabled={!file || busy} onClick={() => void upload()}>
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── what a refusal says ───────────────────────────────────────────────────────────────── */

function keyOf(placement: IContentPlacement): string {
  return `${placement.kind}:${placement.itemId}`;
}

/** survey-api's three refusals of a file, each in the words of the thing to change. */
function uploadRefusal(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isProblem(ProblemType.imageEmpty)) return 'That file is empty.';
    if (error.isProblem(ProblemType.imageTooLarge))
      return 'That file is larger than 5 MB — export or crop it smaller and try again.';
    if (error.isProblem(ProblemType.imageUnsupportedType))
      return 'That is not a JPEG, PNG, GIF or WebP, or it is damaged past its header.';
  }
  return refusalOf(error, 'The upload did not land.');
}

/**
 * A refused write, in one sentence. `invalid-transition` carries survey-api's own `reason`, which
 * names the places an image is still used — the one refusal here an editor can act on without
 * guessing, so it is passed through rather than summarised.
 */
function refusalOf(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.isProblem(ProblemType.invalidTransition) && error.problem?.reason)
      return error.problem.reason;
    if (error.isProblem(ProblemType.revisionConflict))
      return 'Somebody changed this image while the screen was open. Reload and try again.';
    const problems = error.problem?.problems;
    if (problems?.length) return problems[0].reason;
  }
  return fallback;
}
